import React, { FC, useEffect, useState } from 'react'
import DataTable from 'react-data-table-component'
import { useAppDispatch, useAppSelector } from 'src/redux/storeHooks'
import TableLoadingShell from 'src/utils/TableLoadingShell'
import { formatDateLocal, formatTimeZone } from 'src/utils/TimeZoneDateFormat'
import './GroupedTable.scss'
import { deleteProductAsync, getListingProductsAsync } from 'src/redux/actions/omsProductActions'
import { usePermission } from 'src/hooks/usePermission'
import { KTIcon } from 'src/marketConnect/helpers'
import CommonAlertModal from 'src/utils/modals/CommonAlertModal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle, faPlus } from '@fortawesome/free-solid-svg-icons'
import ProductAllVariantsModal from './ProductAllVariantModal'
import { useNavigate } from 'react-router-dom'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { toast } from 'react-toastify'

// import your modal component here
// import ProductVariantsModal from './ProductVariantsModal'

type GroupedTableProps = {
  id?: string
  platformId?: string
  currentChannelData?: any
  mainStore?: boolean
  accountingStore?: boolean
  shippingStore?: boolean
  erpStore?: boolean
  canShowCreateAndActions?: boolean
}

type ListingRow = {
  id: number
  total_variants?: number
  sku?: string
  total_available_quantity?: number
  total_reserve_quantity?: number
  name?: string
  description?: string
  short_description?: string | null
  product_type?: string
  vendor?: string
  api_id?: string
  api_id2?: string | null
  api_code?: string
  api_status?: string
  api_created_at?: string
  api_updated_at?: string
  tags?: string
  isSyncReady?: boolean
  isActive?: boolean
  isDeleted?: boolean
  sync_status?: string
  sync_message?: string | null
  isCreatedByCommercium?: boolean
  type?: string
  created_at?: string
  updated_at?: string
  parent?: number | null
  platform?: number
  user?: number
  [key: string]: any
}

const GroupedTableProducts: FC<GroupedTableProps> = ({ id }) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate();
  const { isWrite, isDelete } = usePermission()

  const { loading, groupedProductList, pagination: reduxPagination } = useAppSelector(
    (state: any) => state.groupedProductSlice
  )

  const [tablePage, setTablePage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState<ListingRow | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deletingRowId, setDeletingRowId] = useState<number | null>(null)

  const [showVariantsModal, setShowVariantsModal] = useState(false)
  const [selectedVariantProduct, setSelectedVariantProduct] = useState<ListingRow | null>(null)
  const [productId, setProductId] = useState<number | null>(null)

  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    search_string: '',
    sort_by: '',
    sort_order: '',
  })

  useEffect(() => {
    if (!id) return

    dispatch(
      getListingProductsAsync({
        platformId: Number(id),
        page: filters.page,
        limit: filters.limit,
        search_string: filters.search_string,
        sort_by: filters.sort_by,
        sort_order: filters.sort_order,
      })
    )
  }, [dispatch, id, filters.page,
    filters.limit,
    filters.search_string,
    filters.sort_by,
    filters.sort_order,
  ])

  const handleReset = () => {
    setSearchInput('')
    setTablePage(1)
    setFilters({
      page: 1,
      limit: 10,
      search_string: '',
      sort_by: '',
      sort_order: '',
    })
  }

  const handleSort = (column: any, sortDirection: 'asc' | 'desc') => {
    setFilters((prev) => ({
      ...prev,
      page: 1,
      sort_by: column.sortField || '',
      sort_order: sortDirection.toUpperCase(),
    }))
    setTablePage(1)
  }

  const handleDeleteClick = (row: ListingRow) => {
    setSelectedProduct(row)
    setShowDeleteModal(true)
  }

  const handleDeleteCancel = () => {
    if (isDeleting) return
    setShowDeleteModal(false)
    setSelectedProduct(null)
    setDeletingRowId(null)
  }

  const handleDeleteConfirm = async () => {
    if (!selectedProduct) return

    try {
      setIsDeleting(true)
      setDeletingRowId(selectedProduct.id)

      const res: any = await dispatch(
        deleteProductAsync({ id: Number(selectedProduct.id) })
      ).unwrap()

      const status = Number(res?.status)
      const apiMessage =
        res?.message ||
        res?.data?.message ||
        res?.errors?.detail ||
        'Product deleted successfully'

      if (status === 200 || status === 201) {
        toast.success(apiMessage)
        setShowDeleteModal(false)
        setSelectedProduct(null)

        dispatch( 
          getListingProductsAsync({
            platformId: Number(id),
            page: filters.page,
            limit: filters.limit,
            search_string: filters.search_string,
            sort_by: filters.sort_by,
            sort_order: filters.sort_order,
          })
        )
      } else {
        toast.error(
          res?.message ||
          res?.data?.message ||
          res?.errors?.detail ||
          'Failed to delete product' 
        )
      }
    } catch (error: any) {
      toast.error(
        error?.data?.message ||
        error?.data?.errors?.detail ||
        error?.message ||
        'Delete failed'
      )
      console.error('Delete failed:', error)
    } finally {
      setIsDeleting(false)
      setDeletingRowId(null)
    }
  }

  const handleEditProduct = (row: ListingRow) => {
    console.log('Edit row:', row)
    navigate(`/update-product/platform/${Number(id)}/productId/${row?.id}`, { state: { mode: "edit", rowData: row } });
  }

  const handleOpenVariantsModal = (row: ListingRow) => {
    console.log('Opening variants modal for product:', row)
    setProductId(row?.id)
    setSelectedVariantProduct(row)
    setShowVariantsModal(true)
  } 

  const handleCloseVariantsModal = () => {
    setShowVariantsModal(false) 
    setSelectedVariantProduct(null)
  }

  const columns: any = [ 
    {
      name: 'Product Name',
      selector: (row: ListingRow) => row?.name || '-', 
      cell: (row: ListingRow) => (
        <div>
          <h6 className='mb-0 fw-bold text-preserve-spacing'>{row?.name || '-'}</h6>
          {/* <small className='text-muted'>{row?.api_code || '-'}</small> */}
        </div>
      ),
      sortable: true,
      sortField: 'name',
      grow: 2,
      minWidth: '240px',
    },
 
    {
      name: 'SKU',
      selector: (row: ListingRow) => row?.sku || '-',
      cell: (row: ListingRow) => <span>{row?.sku || '-'}</span>,
      sortable: true,
      sortField: 'sku', 
      center: true,
      width: '160px',
    },

    {
      name: 'Variants',
      selector: (row: ListingRow) => row?.total_variants ?? 0,
      cell: (row: ListingRow) => {
        const totalVariants = row?.total_variants ?? 0
        const locked = Boolean(row?.isCreatedByCommercium)
 
        return (
          <button
            type="button"
            className={`btn variant-btn-table ${locked ? 'variant-btn-enabled' : 'variant-btn-disabled'}`}
            title={
              !locked
                ? 'Variants managed by Commercium'
                : totalVariants > 0
                  ? 'View Variants'
                  : 'Add Variant'
            } 
            disabled={!locked}
            onClick={() => {
              if (!locked) return
              if (totalVariants === 0) {
                handleEditProduct(row)
              } else {
                handleOpenVariantsModal(row)
              }
            }}
          >
            {totalVariants > 0 ? (
              <> 
                {totalVariants} {totalVariants === 1 ? 'Variant' : 'Variants'} 
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faPlus} className="me-2 variant-btn-icon" />
                Add Variant
              </>
            )}
          </button>
        ) 
      },
      sortable: true, 
      sortField: 'total_variants',
      center: true,
      minWidth: '190px',
    },

    {
      name: 'Available Qty',
      selector: (row: ListingRow) => row?.total_available_quantity ?? 0,
      cell: (row: ListingRow) => (
        <span>{row?.total_available_quantity ?? 0}</span>
      ),
      sortable: true,
      sortField: 'total_available_quantity',
      center: true,
      width: '170px',
    },

    {
      name: (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
            }}
          >
            Created Date{' '}
            <OverlayTrigger 
              placement='top'
              overlay={<Tooltip>{formatTimeZone()}</Tooltip>}
            >
              <span style={{ cursor: 'pointer' }}>
                <FontAwesomeIcon icon={faInfoCircle} />
              </span>
            </OverlayTrigger>
          </div>
        </>
      ),
      cell: (row: ListingRow) => ( 
        <span>{row?.created_at ? formatDateLocal(row.created_at) : '-'}</span>
      ),
      sortable: true,
      sortField: 'created_at',
      center: true,
      width: '180px',
    },

    {
      name: (
        <>
          <div
            style={{ 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
            }}
          >
            Updated Date{' '}
            <OverlayTrigger
              placement='top'
              overlay={<Tooltip>{formatTimeZone()}</Tooltip>}
            >
              <span style={{ cursor: 'pointer' }}>
                <FontAwesomeIcon icon={faInfoCircle} />
              </span>
            </OverlayTrigger>
          </div>
        </>
      ),
      cell: (row: ListingRow) => (
        <span>{row?.updated_at ? formatDateLocal(row.updated_at) : '-'}</span>
      ),
      sortable: true,
      sortField: 'updated_at',
      center: true,
      width: '180px',
    },

    {
      name: 'Actions',
      cell: (row: ListingRow) => {
        const isThisRowDeleting = deletingRowId === row.id && isDeleting
        const locked = Boolean(row?.isCreatedByCommercium)
     
        return locked ? (
          <div className='d-flex gap-2'>
            {isWrite && (
              <button
                type='button'
                className='btn btn-sm btn-light-primary'
                disabled={isDeleting}
                onClick={() => handleEditProduct(row)}
                title='Edit'
              >
                <KTIcon iconName='pencil' className='fs-4' />
              </button>
            )}

            {isDelete && (
              <button
                type='button'
                className='btn btn-sm btn-light-danger'
                disabled={isDeleting}
                onClick={() => handleDeleteClick(row)}
                title='Delete'
              >
                {isThisRowDeleting ? (
                  <span
                    className='spinner-border spinner-border-sm'
                    role='status'
                    aria-hidden='true'
                  />
                ) : (
                  <KTIcon iconName='trash' className='fs-4' />
                )}
              </button>
            )}
          </div>
        ) : null
      },
      center: true,
      width: '130px',
    }
  ]

  return (
    <>
      <div className='card card-flush'>
        <div className='card-header'>
          <div className='card-title'>
            <input
              className='form-control mr-sm-2 input-sm'
              type='search'
              placeholder='Search'
              aria-label='Search'
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setTablePage(1)
                  setFilters((prev) => ({
                    ...prev,
                    page: 1,
                    search_string: searchInput,
                  }))
                }
              }}
            />
          </div>

          <div className='card-toolbar'>
            <div className='d-flex flex-md-row flex-column justify-content-md-end'>
              <div className='mx-md-2 my-1 my-md-0'>
                <button
                  className='btn btn-sm btn-primary w-100px fw-bold py-2'
                  onClick={handleReset}
                >
                  Reset Filters
                </button>
              </div>
            </div>
          </div>
        </div>

        <TableLoadingShell loading={loading} minHeight="50vh">
          <DataTable
            pagination
            paginationServer
            paginationTotalRows={reduxPagination?.count || 0}
            paginationDefaultPage={tablePage}
            paginationPerPage={filters.limit}
            onChangePage={(page) => {
              setTablePage(page)
              setFilters((prev) => ({
                ...prev,
                page,
              }))
            }}
            onChangeRowsPerPage={(perPage, page) => {
              setTablePage(page)
              setFilters((prev) => ({
                ...prev,
                limit: perPage,
                page,
              }))
            }}
            key={`grouped-product-table-${tablePage}-${filters.limit}-${filters.search_string}`}
            fixedHeader
            columns={columns}
            noDataComponent={
              <div className='text-secondary'>
                <h5>No records found</h5>
              </div>
            }
            persistTableHead
            progressPending={false}
            onSort={handleSort}
            data={groupedProductList || []}
            customStyles={{
              table: {
                style: {
                  height: '50vh',
                },
              },
              headCells: {
                style: { fontWeight: 'bold', fontSize: '14px' },
              },
              cells: {
                style: {
                  padding: '10px',
                  borderBottom: '1px solid gainsboro',
                  fontWeight: 'bold',
                },
              },
            }}
          />
        </TableLoadingShell>
      </div>

      <CommonAlertModal
        title={`Are you sure you want to delete "${selectedProduct?.name || 'this product'}"?`}
        subtitle='This product will be deleted permanently.'
        isVisible={showDeleteModal}
        isTermsVisible={false}
        onSuccess={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        successBtnTitle={isDeleting ? 'Deleting...' : 'Yes, Delete'}
        cancelBtnTitle='Cancel'
        successBtnClasses='btn btn-sm btn-danger m-2'
        successDisabled={isDeleting}
      />

      {/*
        Separate modal component.
        Inside that modal component, call:
        dispatch(getAllVariantsAsync({ id: Number(product.id) }))
      */}

      {showVariantsModal && selectedVariantProduct && (
        <ProductAllVariantsModal
          show={showVariantsModal}
          onClose={handleCloseVariantsModal}
          product={selectedVariantProduct}
          platformId={id}
          goToAddVariant={(product) => {
            navigate('/variant-create', {
              state: {
                mode: 'add',
                productId,
                // formikData: values,
                // getEditData: editInitialValues,
                // activeVariantIndex: values?.varients_data?.length || 0,
              },
            })
          }}
          onEditVariant={(row, product) => {
            console.log('Edit variant:', row, product)
            const editVariantId = row?.id
            const editProductId = product?.id
            navigate(`/update-variant/platform/${Number(id)}/productId/${productId}/variantId/${editVariantId}`, {
              state: {
                mode: 'edit',
                editProductId,
                // formikData: values,
                // getEditData: editInitialValues,
                // activeVariantIndex: arrayIndex >= 0 ? arrayIndex : 0,
                editVariantId,
              },
            })
          }}
          onDeleteVariant={(variant, product) => {
            console.log('Delete variant:', variant, product)
            // open delete confirmation
          }}
        />
      )}
    </>
  )
}

export default GroupedTableProducts